/**
 * Login page. Three fields: username, password, and a TYPED instance name
 * (no dropdown, per the security model — names are not enumerable). A single
 * generic error covers all failure modes.
 */

import { useState, type FormEvent } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/store/auth";
import { Logo } from "@/components/brand/logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";

export function LoginPage() {
  const login = useAuth((s) => s.login);
  const error = useAuth((s) => s.error);
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? "/";

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [instanceName, setInstanceName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      // Omit instanceName when empty so it isn't sent as "" (which would fail
      // the schema's minLength:1). Sending nothing = SuperAdmin bootstrap mode.
      const inst = instanceName.trim() || undefined;
      await login(username, password, inst);
      navigate(from, { replace: true });
    } catch {
      // error is surfaced from the store; nothing to do here.
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 px-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="space-y-3">
          <div className="flex items-center gap-2.5">
            <Logo className="text-primary h-7 w-[28px]" />
            <span className="font-semibold text-base tracking-tight">
              ServisSearch Admin
            </span>
          </div>
          <div className="space-y-1">
            <CardTitle>Sign in</CardTitle>
            <CardDescription>
              Enter your credentials and the Instance name you were given.
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4" autoComplete="off">
            <div className="space-y-1.5">
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                autoFocus
                autoComplete="username"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="instanceName">Instance name</Label>
              <Input
                id="instanceName"
                type="text"
                value={instanceName}
                onChange={(e) => setInstanceName(e.target.value)}
                placeholder="e.g. US-Dev (SuperAdmins may leave blank)"
                autoCapitalize="off"
                autoCorrect="off"
              />
              <p className="text-xs text-muted-foreground">
                Case-insensitive. SuperAdmins can leave this blank to manage instances first.
              </p>
            </div>

            {error && (
              <p className="text-sm text-destructive" role="alert">
                {error}
              </p>
            )}

            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? "Signing in…" : "Sign in"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
